import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {Autocomplete, Box, Button, IconButton, MenuItem, Select, Stack, TextField, Tooltip} from "@mui/material";
import {alpha, Theme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {
    SurveyConditionOperator,
    SurveyConditionSubject,
    SurveyQuestion,
    SurveyQuestionOption,
    SurveyQuestionVisibilityRule
} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useGuestCategories} from "../../guestCategories/useGuestCategories";
import {surveyMarkdownToPlainText} from "../shared/surveyMarkdownText.util";
import {isScreen} from "../shared/surveyQuestions.util";
import {getSurveyQuestionTypeFacts} from "../shared/surveyQuestionTypeFacts";
import {getSurveyQuestionTypeIcon, getSurveyQuestionTypeLabelKey} from "../shared/surveyQuestionTypes.util";
import {
    booleanValueLabel,
    describeVisibilityRule,
    isCompleteVisibilityRule,
    visibilityConditionSources,
    visibilityOperatorLabelKey,
    visibilityOperators,
    visibilityRuleIssue,
    VisibilitySourceKind,
    visibilitySourceKind
} from "../shared/surveyVisibilityRules.util";
import {PanelSection} from "./SurveyPanelSection";
import {SwitchRow} from "./SurveySwitchRow";
import {withSwitchedOff} from "./surveyEditorQuestions.util";

interface Props {
    question: SurveyQuestion
    questions: SurveyQuestion[]
    language: string
    onChange: (question: SurveyQuestion) => void
    disabled?: boolean
}

interface SourceOption {
    id: string
    question?: SurveyQuestion
}

const GUEST_CATEGORY_SOURCE_ID = '__guestCategory';

const placeholderSx = {
    color: 'text.disabled',
    opacity: 1
} as const;

const inputPlaceholderSx = {
    '& .MuiInputBase-input::placeholder': placeholderSx
} as const;

const ruleLabelSx = {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
} as const;

function SurveyVisibilitySection({question, questions, language, onChange, disabled}: Props) {
    const {t} = useTranslation();
    const {categories, isLoading: categoriesLoading} = useGuestCategories();

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [draft, setDraft] = useState<SurveyQuestionVisibilityRule>({});
    const [editingIndex, setEditingIndex] = useState<number | null>(null);

    useEffect(() => {
        setIsFormOpen(false);
        setDraft({});
        setEditingIndex(null);
    }, [question.id]);

    const rules = question.visibilityRules ?? [];

    const localize = (text?: LocalizedText) => {
        if (!text) return '';
        return text[language] || text['en'] || Object.values(text)[0] || '';
    };

    const questionLabel = (item: SurveyQuestion) => {
        const title = surveyMarkdownToPlainText(localize(item.title)).trim()
            || t(getSurveyQuestionTypeLabelKey(item.type));
        const index = questions
            .filter(candidate => !isScreen(candidate))
            .findIndex(candidate => candidate.id === item.id);

        return index >= 0 ? `${index + 1}. ${title}` : title;
    };

    const optionLabel = (option: SurveyQuestionOption) => localize(option.label).trim() || t('Untitled option');

    const guestCategoryLabel = (id: string) => (
        categories.find(category => category.id === id)?.name.trim() || t('Unavailable guest category')
    );

    const sources = visibilityConditionSources(questions, question.id);
    const draftSubject = draft.subject ?? SurveyConditionSubject.ANSWER;
    const draftSource = questions.find(item => !!item.id && item.id === draft.questionId);
    const isGuestCategoryDraft = draftSubject === SurveyConditionSubject.GUEST_CATEGORY;

    const guestCategoryOptions = categories.filter(category => !!category.id);
    // Still loading counts as offered so the option does not appear a moment after the form opens.
    const offersGuestCategory = categoriesLoading || !!guestCategoryOptions.length || isGuestCategoryDraft;

    const sourceOptions: SourceOption[] = [
        ...sources.map(item => ({id: item.id!, question: item})),
        ...(!isGuestCategoryDraft && draftSource && !sources.some(item => item.id === draftSource.id)
            ? [{id: draftSource.id!, question: draftSource}]
            : []),
        ...(offersGuestCategory ? [{id: GUEST_CATEGORY_SOURCE_ID}] : []),
    ];

    const selectedSource = isGuestCategoryDraft
        ? sourceOptions.find(option => option.id === GUEST_CATEGORY_SOURCE_ID) ?? null
        : sourceOptions.find(option => !!option.question && option.question.id === draft.questionId) ?? null;

    const sourceOptionLabel = (option: SourceOption) => (
        option.question ? questionLabel(option.question) : t('Guest category')
    );

    const kind: VisibilitySourceKind | undefined = isGuestCategoryDraft
        ? 'guestCategory'
        : visibilitySourceKind(draftSource?.type);

    const selectSource = (option: SourceOption | null) => {
        if (!option) {
            setDraft({});
            return;
        }

        if (option.id === GUEST_CATEGORY_SOURCE_ID) {
            setDraft({subject: SurveyConditionSubject.GUEST_CATEGORY});
            return;
        }

        setDraft({subject: SurveyConditionSubject.ANSWER, questionId: option.question?.id});
    };

    const setValue = (partial: Partial<SurveyQuestionVisibilityRule>) => setDraft(previous => ({
        subject: previous.subject,
        questionId: previous.questionId,
        operator: previous.operator,
        ...partial,
    }));

    const startAdd = () => {
        setDraft({});
        setEditingIndex(null);
        setIsFormOpen(true);
    };

    const startEdit = (index: number) => {
        setDraft(rules[index]);
        setEditingIndex(index);
        setIsFormOpen(true);
    };

    const closeForm = () => {
        setDraft({});
        setEditingIndex(null);
        setIsFormOpen(false);
    };

    const removeRule = (index: number) => {
        const next = rules.filter((_, ruleIndex) => ruleIndex !== index);
        onChange({...question, visibilityRules: next.length ? next : undefined});
    };

    const submit = () => {
        if (!isCompleteVisibilityRule(draft, questions)) return;

        const rule: SurveyQuestionVisibilityRule = {...draft, subject: draftSubject};
        const next = [...rules];

        if (editingIndex === null) {
            next.push(rule);
        } else {
            next[editingIndex] = rule;
        }

        onChange({...question, visibilityRules: next});
        closeForm();
    };

    const renderValueInput = () => {
        if (kind === 'guestCategory') {
            const selectedId = draft.guestCategoryId ?? '';
            const isUnavailable = !!selectedId && !guestCategoryOptions.some(item => item.id === selectedId);

            return (
                <Select
                    size={'small'}
                    fullWidth
                    displayEmpty
                    disabled={disabled}
                    value={selectedId}
                    renderValue={(value) => value
                        ? guestCategoryLabel(value as string)
                        : <Box component={'span'} sx={placeholderSx}>{t('Value')}</Box>}
                    onChange={(event) => setValue({guestCategoryId: event.target.value as string})}
                >
                    {guestCategoryOptions.map(category => (
                        <MenuItem key={category.id} value={category.id}>
                            {guestCategoryLabel(category.id!)}
                        </MenuItem>
                    ))}

                    {/* Keeps a category that was renamed away or deleted selectable-looking instead of blank. */}
                    {isUnavailable && (
                        <MenuItem value={selectedId} disabled>
                            {t('Unavailable guest category')}
                        </MenuItem>
                    )}
                </Select>
            );
        }

        if (kind === 'number') {
            const scale = getSurveyQuestionTypeFacts(draftSource?.type)?.scale;

            if (scale) {
                const values = Array.from({length: scale.max - scale.min + 1}, (_, index) => scale.min + index);

                return (
                    <Select
                        size={'small'}
                        fullWidth
                        displayEmpty
                        disabled={disabled}
                        value={draft.numberValue ?? ''}
                        renderValue={(value) => value
                            ? String(value)
                            : <Box component={'span'} sx={placeholderSx}>{t('Value')}</Box>}
                        onChange={(event) => setValue({numberValue: Number(event.target.value)})}
                    >
                        {values.map(value => (
                            <MenuItem key={value} value={value}>{value}</MenuItem>
                        ))}
                    </Select>
                );
            }

            return (
                <TextField
                    size={'small'}
                    fullWidth
                    type={'number'}
                    disabled={disabled}
                    placeholder={t('Value')}
                    sx={inputPlaceholderSx}
                    value={draft.numberValue ?? ''}
                    onChange={(event) => setValue({
                        numberValue: event.target.value === '' ? undefined : Number(event.target.value)
                    })}
                />
            );
        }

        if (kind === 'boolean') {
            return (
                <Select
                    size={'small'}
                    fullWidth
                    displayEmpty
                    disabled={disabled}
                    value={draft.booleanValue === undefined ? '' : String(draft.booleanValue)}
                    renderValue={(value) => value
                        ? booleanValueLabel(draftSource, value === 'true', t)
                        : <Box component={'span'} sx={placeholderSx}>{t('Value')}</Box>}
                    onChange={(event) => setValue({booleanValue: event.target.value === 'true'})}
                >
                    <MenuItem value={'true'}>{booleanValueLabel(draftSource, true, t)}</MenuItem>
                    <MenuItem value={'false'}>{booleanValueLabel(draftSource, false, t)}</MenuItem>
                </Select>
            );
        }

        if (kind === 'choice') {
            const options = (draftSource?.options ?? []).filter(option => !!option.id);
            const selected = options.filter(option => (draft.optionIds ?? []).includes(option.id!));

            return (
                <Autocomplete
                    multiple
                    size={'small'}
                    fullWidth
                    disabled={disabled}
                    options={options}
                    value={selected}
                    getOptionLabel={optionLabel}
                    isOptionEqualToValue={(left, right) => left.id === right.id}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={selected.length ? undefined : t('Value')}
                            sx={inputPlaceholderSx}
                        />
                    )}
                    onChange={(_, values) => setValue({
                        optionIds: values.map(option => option.id).filter((id): id is string => !!id)
                    })}
                />
            );
        }

        return null;
    };

    const renderRule = (rule: SurveyQuestionVisibilityRule, index: number) => {
        const issue = visibilityRuleIssue(rule, questions, question.id);
        const isMissing = issue === 'sourceMissing';
        const label = isMissing
            ? t('Deleted question')
            : describeVisibilityRule(rule, questions, language, t, categories);
        const issueHint = issue === 'sourceOff'
            ? t('This condition refers to a question that is switched off, so guests never answer it.')
            : issue === 'sourceAfter'
                ? t('This condition refers to a later question, which is still unanswered at this point.')
                : undefined;

        return (
            <Stack
                key={index}
                direction={'row'}
                alignItems={'center'}
                gap={0.5}
                sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    border: (theme: Theme) => (isMissing
                        ? `1px solid ${theme.palette.error.main}`
                        : `1px solid ${alpha(theme.palette.text.primary, 0.12)}`),
                }}
            >
                {!!issueHint && (
                    <Tooltip title={issueHint}>
                        <WarningAmberIcon fontSize={'small'} sx={{color: 'warning.main'}}/>
                    </Tooltip>
                )}

                <Typography
                    variant={'body2'}
                    title={label}
                    sx={{...ruleLabelSx, color: isMissing ? 'error.main' : undefined}}
                >
                    {label}
                </Typography>

                {!isMissing && (
                    <Tooltip title={t('Edit')}>
                        <span>
                            <IconButton size={'small'} disabled={disabled} onClick={() => startEdit(index)}>
                                <EditOutlinedIcon fontSize={'small'}/>
                            </IconButton>
                        </span>
                    </Tooltip>
                )}

                <Tooltip title={t('Remove')}>
                    <span>
                        <IconButton
                            size={'small'}
                            color={'error'}
                            disabled={disabled}
                            onClick={() => removeRule(index)}
                        >
                            <ClearIcon fontSize={'small'}/>
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        );
    };

    const switchedOff = !!question.switchedOff;

    /*
        Off keeps the question, its answers and its conditions; publishing leaves it out. The conditions below stay
        editable, ready for when it is switched on again.
    */
    const visibleSwitch = (
        <SwitchRow
            checked={!switchedOff}
            onChange={(checked) => onChange(withSwitchedOff(question, !checked))}
            label={t('Visible to guests')}
            tooltip={t('Turn off to hide this question from guests without deleting it. Its answers stay in Evaluation.')}
            disabled={disabled}
        />
    );

    const listMode = (
        <Stack gap={1}>
            {rules.length ? (
                <Stack gap={0.5}>
                    {rules.map(renderRule)}
                </Stack>
            ) : switchedOff ? (
                <Typography variant={'body2'} color={'text.secondary'}>
                    {t('Switched off - guests don\'t see this question')}
                </Typography>
            ) : (
                <Stack gap={0.5}>
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {t('This question is always shown.')}
                    </Typography>
                    <Typography variant={'caption'} color={'text.secondary'}>
                        {t('A question whose conditions are not met is skipped.')}
                    </Typography>
                </Stack>
            )}

            {rules.length > 1 && (
                <Typography variant={'caption'} color={'text.secondary'}>
                    {t('All conditions must be met.')}
                </Typography>
            )}

            <Button
                type={'button'}
                size={'small'}
                color={'inherit'}
                startIcon={<AddIcon/>}
                disabled={disabled}
                onClick={startAdd}
                sx={{alignSelf: 'flex-start', justifyContent: 'flex-start', textTransform: 'none'}}
            >
                {t('Add condition')}
            </Button>
        </Stack>
    );

    const formMode = (
        <Stack gap={1}>
            <Autocomplete
                size={'small'}
                disabled={disabled}
                options={sourceOptions}
                value={selectedSource}
                onChange={(_, option) => selectSource(option)}
                getOptionLabel={sourceOptionLabel}
                isOptionEqualToValue={(left, right) => left.id === right.id}
                renderOption={(props, option) => (
                    <Box component={'li'} {...props} key={option.id}>
                        <Stack direction={'row'} alignItems={'center'} gap={1} sx={{minWidth: 0}}>
                            {option.question
                                ? getSurveyQuestionTypeIcon(option.question.type, 18)
                                : <PersonOutlineIcon sx={{fontSize: 18, color: 'text.secondary'}}/>}
                            <Typography variant={'body2'} noWrap>
                                {sourceOptionLabel(option)}
                            </Typography>
                        </Stack>
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={t('Show this question when')}
                        sx={inputPlaceholderSx}
                    />
                )}
            />

            <Tooltip title={kind ? '' : t('Select a source first')}>
                <Box>
                    <Select
                        size={'small'}
                        fullWidth
                        displayEmpty
                        disabled={disabled || !kind}
                        value={draft.operator ?? ''}
                        renderValue={(value) => value && kind
                            ? t(visibilityOperatorLabelKey(value as SurveyConditionOperator, kind))
                            : <Box component={'span'} sx={placeholderSx}>{t('Operator')}</Box>}
                        onChange={(event) => setDraft(previous => ({
                            ...previous,
                            operator: event.target.value as SurveyConditionOperator,
                        }))}
                    >
                        {(kind ? visibilityOperators(kind) : []).map(operator => (
                            <MenuItem key={operator} value={operator}>
                                {t(visibilityOperatorLabelKey(operator, kind!))}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Tooltip>

            {renderValueInput()}

            <Stack direction={'row'} justifyContent={'flex-end'} gap={1}>
                <Button size={'small'} onClick={closeForm} sx={{textTransform: 'none'}}>
                    {t('Cancel')}
                </Button>
                <Button
                    size={'small'}
                    variant={'contained'}
                    disabled={disabled || !isCompleteVisibilityRule(draft, questions)}
                    onClick={submit}
                    sx={{textTransform: 'none'}}
                >
                    {editingIndex === null ? t('Add condition') : t('Update condition')}
                </Button>
            </Stack>
        </Stack>
    );

    return (
        <PanelSection title={t('Visibility')}>
            {visibleSwitch}
            {isFormOpen ? formMode : listMode}
        </PanelSection>
    );
}

export default SurveyVisibilitySection;
